import React, { useState, useEffect } from 'react';
import { ChevronDown, Heart, Music, Video, ExternalLink, Menu, X, Quote } from 'lucide-react';
import MusicProduct from './components/MusicProduct';
import VideoProduct from './components/VideoProduct';
import AfricanPattern from './components/AfricanPattern';
import siteContent from './data/siteContent';
import './styles/main.css';

const App = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState('50'); // Default donation amount
  const [volunteerSubmitted, setVolunteerSubmitted] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Extract data from siteContent
  const {
    config,
    navigation,
    hero,
    about,
    founderStory,
    impact,
    projects,
    activities,
    publications,
    partnerships,
    volunteer,
    donate,
    footer,
  } = siteContent;

  const handleVolunteerSubmit = (e) => {
    e.preventDefault();
    // ⭐ REPLACE this with a real submission handler (e.g. a Formspree endpoint or backend call)
    setVolunteerSubmitted(true);
  };

  return (
    <div className="achh-website">
      <AfricanPattern />

      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="logo">
            {config.logoUrl && (
              <img src={config.logoUrl} alt={config.siteFullName} className="logo-image" />
            )}
            <span className="logo-text">
              {config.showFullName ? config.siteFullName : config.siteName}
            </span>
          </div>

          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <ul className={`nav-links ${mobileMenuOpen ? 'open' : ''}`}>
            {navigation.links.map((link, index) => (
              <li key={index}>
                <a
                  href={link.href}
                  className={[
                    link.isButton ? 'nav-donate-btn' : '',
                    activeSection === link.href.substring(1) ? 'active-link' : '',
                  ].join(' ').trim()}
                  onClick={() => {
                    setActiveSection(link.href.substring(1));
                    setMobileMenuOpen(false);
                  }}
                >
                  {link.isButton && <Heart size={16} />}
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="title-line">{hero.title.line1}</span>
            <span className="title-line">{hero.title.line2}</span>
          </h1>
          <p className="hero-subtitle">{hero.subtitle}</p>
          <div className="hero-cta">
            <a href={hero.buttons.primary.link} className="cta-btn primary">
              {hero.buttons.primary.text}
            </a>
            <a href={hero.buttons.secondary.link} className="cta-btn secondary">
              {hero.buttons.secondary.text}
            </a>
          </div>
        </div>
        <div className="hero-decoration">
          <img src="/images/hero/decoration1.png" alt="" className="doodle-img doodle-1" />
          <img src="/images/hero/decoration2.png" alt="" className="doodle-img doodle-2" />
          <img src="/images/hero/decoration3.png" alt="" className="doodle-img doodle-3" />
          <img src="/images/hero/decoration4.png" alt="" className="doodle-img doodle-4" />
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
        </div>
        <a href="#about" className="scroll-indicator">
          <ChevronDown size={32} />
        </a>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{about.title}</h2>
            <div className="title-underline"></div>
            <img src="https://cdn.doodleipsum.com/700/hand-drawn?bg=DAA520&i=mission-doodle" alt="" className="section-doodle" />
          </div>
          <div className="about-content about-content-single">
            <div className="about-text">
              {about.paragraphs.map((paragraph, index) => (
                <p key={index} className={index === 0 ? "lead-text" : ""}>
                  {paragraph}
                </p>
              ))}
              <img src="https://cdn.doodleipsum.com/700/avatar-2?bg=E27D5F&i=about-doodle-inline" alt="" className="inline-doodle" />
            </div>
          </div>
        </div>
      </section>

      {/* Founder's Story Section */}
      <section id="founder-story" className="founder-story">
        <div className="container">
          <div className="founder-content">
            <div className="founder-photo-wrap">
              <img src={founderStory.photoUrl} alt={founderStory.name} className="founder-photo" />
            </div>
            <div className="founder-text">
              <span className="founder-eyebrow">{founderStory.eyebrow}</span>
              <h2 className="section-title founder-title">{founderStory.title}</h2>
              <div className="title-underline left-aligned"></div>
              {founderStory.paragraphs.map((paragraph, index) => (
                <p key={index} className="founder-paragraph">
                  {paragraph}
                </p>
              ))}
              <blockquote className="founder-quote">
                <Quote size={28} className="founder-quote-icon" />
                <p>{founderStory.quote}</p>
                <footer>
                  <span className="founder-name">{founderStory.name}</span>
                  <span className="founder-role">{founderStory.role}</span>
                </footer>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section id="impact" className="impact">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{impact.title}</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">{impact.subtitle}</p>
          </div>
          <p className="impact-statement">{impact.statement}</p>
          <div className="impact-grid">
            {impact.stats.map((stat, index) => (
              <div key={index} className="impact-card" style={{ animationDelay: `${index * 0.08}s` }}>
                <div className="impact-number">{stat.number}</div>
                <div className="impact-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Projects Section */}
      <section id="projects" className="projects">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{projects.title}</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">{projects.subtitle}</p>
          </div>
          <div className="projects-grid">
            {projects.items.map((project, index) => (
              <div
                key={index}
                className={`project-card accent-${project.accent}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="project-icon">{project.icon}</div>
                <span className="project-status">{project.status}</span>
                <h3 className="project-category">{project.category}</h3>
                <p className="project-description">{project.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Activities / Get Involved Section */}
      <section id="activities" className="activities">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{activities.title}</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">{activities.subtitle}</p>
            <img src="https://cdn.doodleipsum.com/700/abstract?bg=DAA520&i=activities-header-doodle" alt="" className="section-doodle" />
          </div>
          <div className="activities-grid">
            {activities.items.map((activity, index) => (
              <div key={index} className="activity-card" style={{ animationDelay: `${index * 0.1}s` }}>
                {activity.imageUrl ? (
                  <div className="activity-image-container">
                    <img src={activity.imageUrl} alt={activity.title} className="activity-image" />
                  </div>
                ) : (
                  <div className="activity-icon">{activity.icon}</div>
                )}
                <h3 className="activity-title">{activity.title}</h3>
                <p className="activity-description">{activity.description}</p>
                <a href="#contact" className="activity-link">
                  Learn More <ExternalLink size={16} />
                </a>
              </div>
            ))}
          </div>
          <img src="https://cdn.doodleipsum.com/700/avatar-5?bg=5D3A1A&i=activities-bottom-doodle" alt="" className="section-bottom-doodle" />
        </div>
      </section>

      {/* Current Publications Section */}
      <section id="publications" className="publications">
        <div className="container-wide">
          <div className="section-header">
            <h2 className="section-title">{publications.title}</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">{publications.subtitle}</p>
            <img src="https://cdn.doodleipsum.com/700/hand-drawn?bg=DAA520&i=publications-header-doodle" alt="" className="section-doodle" />
          </div>

          {/* DVDs */}
          <div className="publication-category">
            <div className="category-header">
              <Video size={32} />
              <h3 className="category-title">DVDs</h3>
              <img src="https://cdn.doodleipsum.com/300/abstract?bg=E27D5F&i=dvds-doodle" alt="" className="category-doodle" />
            </div>
            <div className="products-grid">
              {publications.dvds.map((dvd, index) => (
                <VideoProduct key={index} {...dvd} />
              ))}
            </div>
          </div>

          {/* Folktale CDs */}
          <div className="publication-category">
            <div className="category-header">
              <Music size={32} />
              <h3 className="category-title">Folktale CDs</h3>
              <img src="https://cdn.doodleipsum.com/300/face?bg=DAA520&i=folktales-doodle" alt="" className="category-doodle" />
            </div>
            <div className="products-grid">
              {publications.folktaleCDs.map((cd, index) => (
                <MusicProduct key={index} {...cd} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partnerships Section */}
      <section id="partnerships" className="partnerships">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{partnerships.title}</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">{partnerships.subtitle}</p>
          </div>
          <div className="partnerships-grid">
            {partnerships.items.map((partner, index) => (
              <div key={index} className="partner-card">
                <h3 className="partner-name">{partner.name}</h3>
                <span className="partner-role">{partner.role}</span>
                <p className="partner-description">{partner.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Volunteer Section */}
      <section id="volunteer" className="volunteer">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{volunteer.title}</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">{volunteer.subtitle}</p>
          </div>

          <div className="volunteer-content">
            <div className="volunteer-info">
              <h3>Ways to Help</h3>
              <ul className="volunteer-ways">
                {volunteer.waysToHelp.map((way, index) => (
                  <li key={index}>
                    <Heart size={20} className="benefit-icon" />
                    <span>{way}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="volunteer-form-container">
              {volunteerSubmitted ? (
                <div className="volunteer-success">
                  <Heart size={32} />
                  <h3>Thank you!</h3>
                  <p>We've received your interest in volunteering and will be in touch soon.</p>
                </div>
              ) : (
                <form className="volunteer-form" onSubmit={handleVolunteerSubmit}>
                  <input type="text" placeholder="Your Name" required />
                  <input type="email" placeholder="Your Email" required />
                  <select defaultValue="" required>
                    <option value="" disabled>Area of Interest</option>
                    {volunteer.interestOptions.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                  <input type="text" placeholder="Your Availability (e.g. weekends, remote)" />
                  <textarea placeholder="Tell us why you'd like to volunteer" rows="4" required></textarea>
                  <button type="submit" className="cta-btn primary">{volunteer.submitText}</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Donate Section */}
      <section id="donate" className="donate">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{donate.title}</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">{donate.subtitle}</p>
            <img src="https://cdn.doodleipsum.com/700/avatar?bg=DAA520&i=donate-header-doodle" alt="" className="section-doodle section-doodle-light" />
          </div>

          <div className="donate-content">
            <div className="donate-info">
              <img src="https://cdn.doodleipsum.com/400/face?bg=E27D5F&i=donate-side-doodle" alt="" className="donate-side-doodle" />
              <h3>{donate.infoTitle}</h3>
              <ul className="donate-benefits">
                {donate.benefits.map((benefit, index) => (
                  <li key={index}>
                    <Heart size={20} className="benefit-icon" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="donate-form-container">
              <img src="https://cdn.doodleipsum.com/300/hand-drawn?bg=DAA520&i=donate-form-doodle" alt="" className="form-doodle" />
              <div className="donation-amounts">
                <h4>{donate.amountTitle}</h4>
                <div className="amount-grid">
                  {donate.amounts.map((amount, index) => (
                    <button
                      key={index}
                      className={`amount-btn ${selectedAmount === amount.value && amount.label !== 'Custom' ? 'active' : ''}`}
                      onClick={() => {
                        if (amount.label !== 'Custom') {
                          setSelectedAmount(amount.value);
                        }
                      }}
                      type="button"
                    >
                      {amount.label}
                    </button>
                  ))}
                </div>
                <p className="donation-impact-note">
                  {donate.amounts.find((a) => a.value === selectedAmount)?.impact}
                </p>
              </div>

              <form action="https://www.paypal.com/donate" method="post" target="_blank" className="donate-form">
                <input type="hidden" name="business" value={config.paypalEmail} />
                <input type="hidden" name="no_recurring" value="0" />
                <input type="hidden" name="item_name" value="Support The African Cultural Heritage Hub Mission" />
                <input type="hidden" name="amount" value={selectedAmount} />
                <input type="hidden" name="currency_code" value="USD" />

                <button type="submit" className="donate-btn">
                  <Heart size={20} />
                  {donate.buttonText}
                </button>
              </form>

              <p className="donate-note">{donate.taxNote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Get in Touch</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">Have questions? Want to get involved? We'd love to hear from you!</p>
          </div>

          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-item">
                <h3>📧 Email</h3>
                <p><a href={`mailto:${config.contactEmail}`}>{config.contactEmail}</a></p>
              </div>
              <div className="contact-item">
                <h3>📞 Phone</h3>
                <p><a href={`tel:${config.contactPhone}`}>{config.contactPhone}</a></p>
              </div>
            </div>

            <div className="contact-form">
              <form>
                <input type="text" placeholder="Your Name" required />
                <input type="email" placeholder="Your Email" required />
                <textarea placeholder="Your Message" rows="5" required></textarea>
                <button type="submit" className="cta-btn primary">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            {footer.sections.map((section, index) => (
              <div key={index} className="footer-section">
                <h4>{section.title}</h4>
                {section.content && <p>{section.content}</p>}
                {section.links && (
                  <ul>
                    {section.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <a href={link.href}>{link.text}</a>
                      </li>
                    ))}
                  </ul>
                )}
                {section.lines && section.lines.map((line, lineIndex) => (
                  <p key={lineIndex}>{line}</p>
                ))}
              </div>
            ))}
          </div>
          <div className="footer-bottom">
            <p>{footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
